import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../data/cloud-data.js';
import { Configuration, SpareWheelOptions, WheelTypes, WheelNutOptions, LuggageRackOptions, SpareWheelCoverOptions } from '../data/configuration.js';
import { CameraController, CameraViews } from '../camera-controller.js';
import { WheelConfigurator } from '../configurator/wheel-configurator.js';
import { AccessoriesConfigurator } from '../configurator/accessories-configurator.js';
import { VehicleView } from './vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from './view-content.js';
import { Utilities } from '../utilities/utilities.js';
import { Object3D } from '@wonderlandengine/api';
import SelectedSectionItem from './ui/selected-section-item.tsx';
import ViewContentRows from './ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface WheelsViewProps {
	wheelConfigurator: WheelConfigurator;
	accessoriesConfigurator: AccessoriesConfigurator;
	cameraController: CameraController;
	onBackButtonClicked: () => void;
	onNextButtonClicked: () => void;
	softTopDownLight: Object3D;
	softTopUpLight: Object3D;
}

interface WheelItemsProps {
	wheelType: WheelTypes;
}

interface WheelsViewContextType {
	selectedWheelItemId: string;
	handleWheelItemClicked: (id: string) => void;
	selectedWheelNutItemId: string;
	handleWheelNutItemClicked: (id: string) => void;
	selectedSpareWheelItemId: string;
	handleSpareWheelItemClicked: (id: string) => void;
	handleCoversItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function WheelsView(props: WheelsViewProps) {

	// initial values.
	const initialWheelItemId = Configuration.wheelStyle;
	const initialWheelItemName = () => getItemName(initialWheelItemId);
	const initialWheelNutItemId = Configuration.wheelNutOption;
	const initialWheelNutItemName = () => getWheelNutItemName(initialWheelNutItemId);
	const initialSpareWheelItemId = Configuration.spareWheelOption;
	const initialSpareWheelItemName = () => getSpareWheelItemName(initialSpareWheelItemId);
	const initialCoversItemIds = () => Array.from(Configuration.accessoryCoversOptions).filter((item) => item === SpareWheelCoverOptions.WheelCover || item === SpareWheelCoverOptions.WheelDisc);

	// the wheels are presented as an accordion of one panel per wheel type, of which the panel holding the selected wheel is the one open on arrival.
	const initialOpenWheelType = () => CloudData.getWheelTypeForId(initialWheelItemId);

	// states.
	const [selectedWheelItemId, setSelectedWheelItemId] = useState(initialWheelItemId);
	const [selectedWheelItemName, setSelectedWheelItemName] = useState(initialWheelItemName);
	const [selectedWheelNutItemId, setSelectedWheelNutItemId] = useState(initialWheelNutItemId);
	const [selectedWheelNutItemName, setSelectedWheelNutItemName] = useState(initialWheelNutItemName);
	const [selectedSpareWheelItemId, setSelectedSpareWheelItemId] = useState(initialSpareWheelItemId);
	const [selectedSpareWheelItemName, setSelectedSpareWheelItemName] = useState(initialSpareWheelItemName);
	const [selectedCoversItemIds, setSelectedCoversItemIds] = useState(initialCoversItemIds);

	// the one wheel type panel currently open, or null when the customer has closed them all, as the paintwork view holds its own
	// accordion. there is no separate 'selected wheel type' any more - the type is whichever one the selected wheel belongs to.
	const [openWheelType, setOpenWheelType] = useState<WheelTypes | null>(initialOpenWheelType);

	const setWheelNutOption = (id: string) => {

		// update the selected item and item name.
		setSelectedWheelNutItemId(id);
		setSelectedWheelNutItemName(getWheelNutItemName(id));

		// update the configuration.
		Configuration.wheelNutOption = id;
		Configuration.updateCosts();

		// select the wheel-nut option on the car.
		props.wheelConfigurator.selectWheelNutOption(id);
	};

	// handlers.

	const handlePanelOpenClose = (wheelTypePanel: WheelTypes, isOpen: boolean) => {

		// only one panel is open at a time, so opening one closes the others, and closing one leaves none open.
		setOpenWheelType(isOpen ? wheelTypePanel : null);
	};

	const handleWheelItemClicked = (id: string) => {

		// if an alloy wheel has been selected, reset the selected wheel-nut option to none, as is not applicable with alloys.
		if (CloudData.getWheelTypeForId(id) === WheelTypes.Alloy) {
			setWheelNutOption(WheelNutOptions.None);
		}

		// or if a wire wheel has been selected, default to the polished wheel-nut option (i.e. if the current selection is none).
		else if (Configuration.wheelNutOption === WheelNutOptions.None) {
			setWheelNutOption(WheelNutOptions.Polished);
		}

		// update the selected item and item name.
		setSelectedWheelItemId(id);
		setSelectedWheelItemName(getItemName(id));

		// update the configuration.
		Configuration.wheelStyle = id;
		Configuration.updateCosts();

		// select the wheel style on the car.
		props.wheelConfigurator.selectWheelStyle(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.LeftWheelView);
	};

	const handleWheelNutItemClicked = (id: string) => {

		// set the wheel-nut option.
		setWheelNutOption(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.LeftWheelView);
	};

	const handleSoftTopLight = (shouldDisplay: boolean) => {

		// toggle soft top brake light for regions outside the USA.
		if (!Configuration.isUsRegion()) {
			Utilities.setActiveAll(props.softTopDownLight, shouldDisplay);
			Utilities.setActiveAll(props.softTopUpLight, shouldDisplay);
		}
	}

	const handleSpareWheelItemClicked = (id: string) => {

		if (Configuration.spareWheelOption === SpareWheelOptions.Spare) {
			id = SpareWheelOptions.None;
		}

		// update the selected item and item name.
		setSelectedSpareWheelItemId(id);
		setSelectedSpareWheelItemName(getSpareWheelItemName(id));

		// update the configuration,
		Configuration.spareWheelOption = id;
		Configuration.updateCosts();

		// select the spare wheel option on the car.
		props.wheelConfigurator.selectSpareWheelOption(id);

		// handle luggage rack options when toggling spare wheel.
		if (Configuration.accessoryAftermarketOptions.has(LuggageRackOptions.Silver)) {
			props.accessoriesConfigurator.selectAftermarketAccessory(LuggageRackOptions.Silver);
		}
		else if (Configuration.accessoryAftermarketOptions.has(LuggageRackOptions.Stowage)) {
			props.accessoriesConfigurator.selectAftermarketAccessory(LuggageRackOptions.Stowage);
		}
		else {
			props.accessoriesConfigurator.selectAftermarketAccessory('');
		} 

		// if the 'no spare wheel' option is chosen, remove any selected spare wheel accessory items.
		if (id === SpareWheelOptions.None) {
			const spareWheelAccessories = CloudData.getSpareWheelAccessories();
			spareWheelAccessories.forEach((item) => {
				Configuration.removeAccessoryOption(Configuration.accessoryCoversOptions, item);
			});

			handleSoftTopLight(true);
		}

		// otherwise, remove the black luggage rack option and pre-equipment pack, as this is not available with a spare wheel.
		else {
			Configuration.removeAccessoryOption(Configuration.accessoryAftermarketOptions, Configuration.blackLuggageRackOption);
			Configuration.removeAccessoryOption(Configuration.accessoryAftermarketOptions, Configuration.preEquipmentPackOption);

			handleSoftTopLight(false);
		}

		props.cameraController.changeCameraView(CameraViews.LuggageRackView);
	};

	const handleCoversItemClicked = (id: string) => {

		// update the configuration.
		Configuration.toggleAccessoryOption(Configuration.accessoryCoversOptions, id);

		const coversArr = Array.from(Configuration.accessoryCoversOptions);
		const spareWheelCoverIds = coversArr.filter((item) => item === SpareWheelCoverOptions.WheelCover || item === SpareWheelCoverOptions.WheelDisc);
		setSelectedCoversItemIds(spareWheelCoverIds);
	}

	// calculate spare wheel section price with wheel and covers.
	const wheelItemPrice = CloudData.getPrice(selectedWheelItemId, CloudData.wheelStyleOptions, { ref: false });
	const spareWheelCoversPrice = CloudData.getAccessoryPrices(new Set(selectedCoversItemIds), { ref: false });
	const spareWheelPrice =
		CloudData.getPrice(CloudData.getSpareWheelId(selectedWheelItemId),
			CloudData.wheelStyleOptions,
			{ ref: false }) -
		wheelItemPrice;
	const spareWheelSectionPrice = spareWheelPrice + spareWheelCoversPrice;

	// return the HTML.
	return (
		<WheelsViewContext.Provider value={{ selectedWheelItemId, handleWheelItemClicked, selectedWheelNutItemId, handleWheelNutItemClicked, selectedSpareWheelItemId, handleSpareWheelItemClicked, handleCoversItemClicked }}>
			<VehicleView
				onBackButtonClicked={props.onBackButtonClicked}
				onNextButtonClicked={props.onNextButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-wheels">
							WHEELS
						</h2>
					</div>

					<SectionPanel
						subHeading={'ALLOY'}
						modalContent={CloudData.getModalContent(selectedWheelItemId, CloudData.wheelStyleOptions)}
						isClosed={openWheelType !== WheelTypes.Alloy}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(WheelTypes.Alloy, isOpen)}
					>
						<SelectedSectionItem
							itemName={selectedWheelItemName}
							sectionPrice={wheelItemPrice}
						/>
						<WheelItems wheelType={WheelTypes.Alloy} />
					</SectionPanel>

					<SectionPanel
						subHeading={'WIRE'}
						modalContent={CloudData.getModalContent(selectedWheelItemId, CloudData.wheelStyleOptions)}
						isClosed={openWheelType !== WheelTypes.Wire}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(WheelTypes.Wire, isOpen)}
					>
						<SelectedSectionItem
							itemName={selectedWheelItemName}
							sectionPrice={wheelItemPrice}
						/>
						<WheelItems wheelType={WheelTypes.Wire} />
					</SectionPanel>

					{WheelTypes.Alloy !== CloudData.getWheelTypeForId(selectedWheelItemId) && (
						<SectionPanel
							subHeading={'WIRE WHEEL NUTS'}
							shouldStartClosed={true}
							modalContent={CloudData.getModalContent(selectedWheelNutItemId, CloudData.wheelNutOptions)}
						>
							<SelectedSectionItem
								itemName={selectedWheelNutItemId !== WheelNutOptions.None
									? selectedWheelNutItemName
									: 'Only available with wire wheels'}
								sectionPrice={CloudData.getPrice(selectedWheelNutItemId, CloudData.wheelNutOptions, { ref: false })}
							/>
							<WheelNutItems />
						</SectionPanel>
					)}

					<SectionPanel
						subHeading={'SPARE WHEEL'}
						shouldStartClosed={true}
					>
						<SelectedSectionItem
							itemName={selectedSpareWheelItemName}
							sectionPrice={selectedSpareWheelItemId !== SpareWheelOptions.None ? spareWheelSectionPrice : 0}
						/>
						<SpareWheeltems />
					</SectionPanel>

				</div>
			</VehicleView>
		</WheelsViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const WheelsViewContext = createContext<WheelsViewContextType | undefined>(undefined);

function WheelItems(props: WheelItemsProps) {
	switch (props.wheelType) {
		case WheelTypes.Alloy:
			return <WheelItemsAlloy />;
		case WheelTypes.Wire:
			return <WheelItemsWire />;
		default:
			return <WheelItemsAlloy />;
	}
};

function WheelItemsAlloy() {
	return (
		<ViewContentRows>
			<WheelItem id={'W005'} imageFilePath={'alloy-wheels/alloy-silver'} />
			<WheelItem id={'W006'} imageFilePath={'alloy-wheels/alloy-black'} />
		</ViewContentRows>
	);
}

function WheelItemsWire() {
	return (
		<ViewContentRows>
			<WheelItem id={'W007'} imageFilePath={'wire-wheels/wire-silver'} />
			<WheelItem id={'W008'} imageFilePath={'wire-wheels/wire-black'} />
			<WheelItem id={'W009'} imageFilePath={'wire-wheels/wire-polished'} />
		</ViewContentRows>
	);
}

function WheelNutItems() {
	return (
		<ViewContentRows>
			<WheelNutItem id={'WS001'} imageFilePath={'wheel-nuts-polished'} />
			<WheelNutItem id={'WS002'} imageFilePath={'wheel-nuts-brass'} />
		</ViewContentRows>
	);
};

function SpareWheeltems() {
	return (
		<ViewContentRows>
			<SpareWheelItem id={SpareWheelOptions.Spare} imageFilePath={'spare'} />
			<SpareWheelCoverItem id={'COV014'} imageFilePath={'spare-wheel-cover'} />
			<SpareWheelCoverItem id={'W016'} imageFilePath={'spare-wheel-disc'}  />
		</ViewContentRows>
	);
};

function WheelItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(WheelsViewContext);
	if (!context) {
		throw new Error('WheelsViewContext must be used within a WheelsView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/wheels/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedWheelItemId}
			onItemClicked={context.handleWheelItemClicked}
		/>
	);
}

function WheelNutItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(WheelsViewContext);
	if (!context) {
		throw new Error('WheelsViewContext must be used within a WheelsView component');
	}

	// the wheel-nut option only applies to wire wheels, so disable if wire wheels are not selected.
	const isDisabled = CloudData.getWheelTypeForId(Configuration.wheelStyle) !== WheelTypes.Wire;

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getWheelNutItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/wheels/wheel-nuts/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedWheelNutItemId}
			isDisabled={isDisabled}
			onItemClicked={context.handleWheelNutItemClicked}
		/>
	);
}

function SpareWheelItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(WheelsViewContext);
	if (!context) {
		throw new Error('WheelsViewContext must be used within a WheelsView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getSpareWheelItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/wheels/spare-wheel/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedSpareWheelItemId}
			onItemClicked={context.handleSpareWheelItemClicked}
		/>
	);
}

function SpareWheelCoverItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(WheelsViewContext);
	if (!context) {
		throw new Error('WheelsViewContext must be used within a AccessoriesView component');
	}

	const getCoverName = (id: string): string => {
		return CloudData.getName(id, CloudData.accessoryOptions);
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getCoverName(props.id)}
			imageFilePath={`assets/images/configuration-items/accessories/${props.imageFilePath}.svg`}
			isSelected={Configuration.accessoryCoversOptions.has(props.id)}
			isDisabled={Configuration.spareWheelOption === SpareWheelOptions.None}
			onItemClicked={context.handleCoversItemClicked}
			showName={true}
			itemPrice={CloudData.getPrice(props.id, CloudData.accessoryOptions, { ref: false })}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.wheelStyleOptions);
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getWheelNutItemName(id: string): string {
	return CloudData.getName(id, CloudData.wheelNutOptions);
}

// returns the item name for the given spare wheel item ID.
function getSpareWheelItemName(id: string): string {
	if (id === SpareWheelOptions.Spare) return 'With spare wheel';
	return 'No spare wheel';
}